import type { TvChannel } from '@setplex/tria-api'
import type { Store } from 'effector'
import type { TvChannelPayment } from '~/features/payment/interfaces'

import { A } from '@mobily/ts-belt'
import { combine, createEvent, createStore, restore, sample } from 'effector'
import { previous } from 'patronum'
import { model as router } from '~/core/router'
import { CategoryId } from '~/entities/epg/categories'
import { epgChannelsModel } from '~/entities/epg/channels'
import { Intent, PlayerIntentMessage } from '~/entities/player'
import { loadEpgCategoriesModel } from '~/features/epg/categories/load'
import { selectEpgCategoryModel } from '~/features/epg/categories/select'
import { loadEpgChannelsModel } from '~/features/epg/channels/load'
import {
  loadMoreEpgChannelsModel,
  LOAD_MORE_COUNT,
} from '~/features/epg/channels/load-more'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { updateEpgChannelModel } from '~/features/epg/channels/update'
import { getEpgPathFromSignInModal } from '~/features/epg/modals/signInToWatch/helpers'
import { model as favoritesFeatureModel } from '~/features/favorites'
import { model as paymentModel } from '~/features/payment'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import * as pinLockFeature from '~/features/pin-lock'
import { model as guestModel } from '~/features/subscriber/guest'
import { $isFullscreen } from '~/shared/effector/fullscreen'
import { aye, F, nay, T } from '~/shared/helpers'
import { model as mq } from '~/shared/mq'
import { epgCategoriesWidgetModel } from '~/widgets/epg/categories'
import { model as signInToWatchPopupModel } from '~/widgets/Popups/SignInToWatchPopup'
import { isChannelLockedAndPrevChannelNotPurchasedAndLocked } from './helpers'

// load initial epg info
export const init = createEvent<{ query: URLSearchParams }>()

// reset widget on epg page left
export const left = createEvent()

// reset widget on sign out
export const reset = createEvent()

// load more channels
export const more = createEvent()

// like/unlike channel
export const likeOrUnlike = updateEpgChannelModel.update

// try to select channel
export const trySelect = createEvent<TvChannel>()

// open pinlock popup output event
export const openPinlockPopup = createEvent<TvChannel>()

// update given channel by ids
export const updateChannels = createEvent<number[]>()

// update given channel set by ids
export const updateChannelSets = createEvent<number[]>()

// currently selected channel
// (it could be channel NOT in active channel list)
export const $selected = selectEpgChannelModel.$active

// probe channel by clicking on it
export const probe = createEvent<number | null>()
// probing channel id
export const $probingId = restore(probe, null).reset([left, reset])
// probing channel
export const $probing: Store<TvChannel | null> = combine(
  $probingId,
  ssePurchasesModel.$channel,
  epgChannelsModel.$all,
  (id, adjust, [channels]) => {
    const ch = id == null ? null : (channels.get(id) ?? null)
    return ch == null ? null : adjust(ch)
  }
)

export const $previouslyUnlockedId = previous(pinLockFeature.$unlockedId)
export const $previouslyUnlockedChannel: Store<TvChannel | null> = combine(
  $previouslyUnlockedId,
  ssePurchasesModel.$channel,
  epgChannelsModel.$all,
  (id, adjust, [channels]) => {
    const ch = id == null ? null : (channels.get(id) ?? null)
    return ch == null ? null : adjust(ch)
  }
)

// currently active category channels
export const $activeList: Store<TvChannel[]> = combine(
  selectEpgCategoryModel.$activeId,
  epgChannelsModel.$byCategory,
  (categoryId, [channels]) => {
    if (categoryId == null) return []
    return channels.get(categoryId) ?? []
  }
)

// true while the selected category's first channel page is loading
export const $rowsLoading: Store<boolean> = combine(
  selectEpgCategoryModel.$activeId,
  $activeList,
  epgCategoriesWidgetModel.$categories,
  loadEpgChannelsModel.$pending,
  (categoryId, activeList, categories, [pending]) => {
    const selectedCategory = categories.find(({ id }) => id === categoryId)

    return (
      categoryId != null &&
      activeList.length === 0 &&
      (Boolean(pending.get(categoryId)) ||
        Boolean(selectedCategory?.channelIds.length))
    )
  }
)

export const $loadMoreRowsCount: Store<number> = combine(
  selectEpgCategoryModel.$activeId,
  epgChannelsModel.$byCategory,
  loadMoreEpgChannelsModel.$pending,
  (categoryId, [channels, meta], [pending]) => {
    if (categoryId == null || !pending.get(categoryId)) return 0

    const loadedCount = channels.get(categoryId)?.length ?? 0
    const categoryMeta = meta.get(categoryId)
    const totalElements = categoryMeta?.totalElements
    const pageSize = categoryMeta?.size ?? LOAD_MORE_COUNT

    if (totalElements == null) return 1

    return Math.max(0, Math.min(pageSize, totalElements - loadedCount))
  }
)

// pipe to check if channel is selected
export const $isChannelSelectedPipe = combine(
  $selected,
  (selected) => (channel?: TvChannel | number | string) => {
    if (selected == null || channel == null) return false
    return typeof channel === 'object'
      ? selected.id === channel.id
      : selected.id === Number(channel)
  }
)

// on every category change request to load initial set of channels, if needed
sample({
  clock: [
    init,
    selectEpgCategoryModel.$activeId,
    loadEpgCategoriesModel.$ready,
  ],
  source: {
    categoryId: selectEpgCategoryModel.$activeId,
    ready: loadEpgCategoriesModel.$ready,
  },
  filter: ({ categoryId, ready }) => categoryId != null && ready,

  fn: ({ categoryId }) => ({ categoryId: categoryId! }),
  target: loadEpgChannelsModel.load,
})

// on request from skin, load more channels for active category
sample({
  clock: more,
  source: selectEpgCategoryModel.$activeId,
  filter: (categoryId: number | null): categoryId is number =>
    categoryId != null,
  fn: (categoryId) => ({ categoryId }),
  target: loadMoreEpgChannelsModel.load,
})

// auto select first or url-chosen channel
sample({
  clock: [init, loadEpgChannelsModel.loadInitialChannelsFx.done, $activeList],
  source: {
    init: restore(init, null).reset(left, reset),
    channels: $activeList,
  },
  filter: ({ init, channels }) => init != null && channels.length > 0,

  fn: ({ init, channels }) => ({ channels, query: init!.query }),
  target: selectEpgChannelModel.autoSetActiveId,
})

// store indicates if current selected channel is auto-selected
export const $isFirstAutoSelect = createStore<boolean | null>(true).reset([
  left,
  reset,
])

// indicates user go from FS player to small one, and do not select any other channel
export const $isFullscreenJustOff = createStore<boolean | null>(false).reset([
  probe,
])

// check channel selected by click event (probe) -> isAutoSelect = false
// (autoselect until probe set id once)
sample({
  clock: $probingId,
  source: $activeList,
  filter: (channels, probingId) => aye(channels.length > 0 && probingId),
  fn: F,
  target: $isFirstAutoSelect,
})

// close pin lock popup (if any) when probing new channel
sample({
  clock: probe,
  target: pinLockFeature.closePinLockPopup,
})

// close external payment popup (if any) when probing new channel
sample({
  clock: probe,
  target: paymentModel.closeExternalPaymentPopup,
})

// close external payment popup (if any) when probing new channel
sample({
  clock: probe,
  target: signInToWatchPopupModel.closeSignInToWatchPopup,
})

// track user turned of fullscreen, probe resets it
sample({
  clock: $isFullscreen,
  filter: (isFullscreen: boolean) => nay(isFullscreen),
  fn: T,
  target: $isFullscreenJustOff,
})

// actually select probing channel if it is not locked (and not selected already)
sample({
  clock: probe,
  source: {
    probing: $probing,
    isLocked: pinLockFeature.$isLockedFn,
  },
  filter: ({ probing, isLocked }) => nay(isLocked(probing)),
  fn: ({ probing }) => probing!,
  target: trySelect,
})

// show pin lock popup if probing channel is locked
sample({
  clock: probe,
  source: {
    probing: $probing,
    isLocked: pinLockFeature.$isLockedFn,
  },
  filter: ({ probing, isLocked }) => aye(isLocked(probing)),

  fn: ({ probing }) => probing!,
  target: openPinlockPopup,
})

// if probing channel got unlocked, select it
sample({
  clock: pinLockFeature.$unlockedId,
  source: {
    probing: $probing,
    isLocked: pinLockFeature.$isLockedFn,
  },
  filter: ({ probing, isLocked }, unlockedId) =>
    nay(isLocked(probing)) && aye(unlockedId),
  fn: ({ probing }) => probing!,
  target: trySelect,
})

// close pin lock popup (if any) when selecting new channel
sample({
  clock: trySelect,
  target: pinLockFeature.closePinLockPopup,
})

// reset unlocked id if channel is not locked and is free or purchased
sample({
  clock: trySelect,
  filter: (channel) =>
    channel != null &&
    nay(channel.locked) &&
    (aye(channel.free) || aye(channel.purchaseInfo)),
  target: [pinLockFeature.resetUnlockId],
})

// if channel is free or purchased and is unlocked, actually select it
const selectChannelByUserClick = createEvent<number>()

sample({
  clock: trySelect,
  source: pinLockFeature.$isLockedFn,
  filter: (isLocked, channel) =>
    channel !== null &&
    nay(isLocked(channel)) &&
    (aye(channel.free) || aye(channel.purchaseInfo)),
  fn: (_, channel) => channel.id,
  target: selectChannelByUserClick,
})

sample({
  clock: selectChannelByUserClick,
  target: selectEpgChannelModel.setActiveId,
})

sample({
  clock: selectChannelByUserClick,
  fn: () => new PlayerIntentMessage(undefined, Intent.SelectChannelFromEpg),
  target: mq.emit,
})

// channel to pay for
const selectTvChannelForPayment = createEvent<TvChannelPayment>()

// prepare channel data to show payment popup
sample({
  clock: trySelect,
  filter: (channel) =>
    channel !== null && nay(channel.free) && nay(channel.purchaseInfo),
  fn: (channel) => ({
    id: channel.id,
    channelNumber: Number(channel.channelNumber),
    categoryId: 0, // "default" value seted as we got custom categories, and redirect not used on /epg
    name: String(channel.name),
    logoUrl: String(channel.logoUrl),
    purchaseInfo: channel.purchaseInfo ?? null,
    free: Boolean(channel.free),
    priceSettings: channel.priceSettings ?? null,
    labels: null,
    featured: false, // featured not used on /epg
    externalId: channel.externalId,
  }),
  target: selectTvChannelForPayment,
})

// prepare redirect data to show popup
sample({
  clock: selectTvChannelForPayment,
  source: {
    guest: guestModel.$guest,
    query: router.$query,
  },
  filter: ({ guest }) => aye(guest),
  fn: ({ query }, channel) => ({
    name: channel.name,
    redirect: getEpgPathFromSignInModal(query),
  }),
  target: signInToWatchPopupModel.openSignInToWatchPopup,
})

sample({
  clock: selectTvChannelForPayment,
  source: guestModel.$guest,
  filter: nay,
  fn: (_, channel) => channel,
  target: paymentModel.selectTvChannelOnEpg,
})

// update channels after successful purchase
sample({
  clock: updateChannels,
  filter: A.isNotEmpty,
  target: epgChannelsModel.loadManyFx,
})

// update channel sets after successful purchase
sample({
  clock: updateChannelSets,
  filter: A.isNotEmpty,
  target: epgChannelsModel.loadSetsFx,
})

// reset unlockedId for not locked channel after successful purchase and closing payment modal
sample({
  clock: paymentModel.closePaymentPopup,
  source: $probing,
  filter: (channel) =>
    channel != null && aye(channel.purchaseInfo) && nay(channel.locked),
  target: [pinLockFeature.resetUnlockId],
})

// select channel after successful purchase and closing payment modal
sample({
  clock: paymentModel.closePaymentPopup,
  source: $probing,
  filter: (channel) => channel != null && aye(channel.purchaseInfo),

  fn: (channel) => channel!.id,
  target: [selectEpgChannelModel.setActiveId],
})

// select channel after successful purchase via shopping cart (on SSE event)
sample({
  clock: ssePurchasesModel.$channel,
  source: {
    init: restore(init, null).reset(left, reset),
    channel: $probing,
  },
  filter: ({ init, channel }, adjust) =>
    init != null && channel != null && aye(adjust(channel).purchaseInfo),

  fn: ({ channel }) => channel!.id,
  target: [selectEpgChannelModel.setActiveId],
})

// unlock previous locked channel, if selected is locked and not purchased
sample({
  clock: paymentModel.closePaymentPopup,
  source: {
    channel: $probing,
    prevChannel: $previouslyUnlockedChannel,
  },
  filter: ({ channel, prevChannel }) =>
    isChannelLockedAndPrevChannelNotPurchasedAndLocked({
      channel,
      prevChannel,
    }),

  fn: ({ prevChannel }) => prevChannel!.id,
  target: pinLockFeature.unlockId,
})

// on any category change, check for "dirty" state on favorite channels, and reload them if needed
sample({
  clock: selectEpgCategoryModel.$activeId,
  filter: (categoryId) => categoryId != null,
  target: updateEpgChannelModel.reloadFavoritesIfNeeded,
})

// if current active category is not favorite, reload favorite channels if needed, when removing channel from favorites
sample({
  clock: [updateEpgChannelModel.remove, updateEpgChannelModel.reload],
  source: selectEpgCategoryModel.$activeId,
  filter: (categoryId) =>
    categoryId != null && categoryId !== CategoryId.Favorite,
  target: updateEpgChannelModel.reloadFavoritesIfNeeded,
})

// on any favorites change OUTSIDE of module -> erase favorites channels
sample({
  clock: favoritesFeatureModel.$favorites,
  source: selectEpgChannelModel.$activeId,
  filter: (activeId) => activeId == null, // `activeId` is set to null when we left module
  target: updateEpgChannelModel.clear,
})

//
// left and reset logic
//

sample({
  clock: left,
  target: [selectEpgChannelModel.$activeId.reinit, updateEpgChannelModel.dirty],
})

// need to create new map, instead of reinit, because this will leave old map in place
sample({
  clock: reset,
  fn: () => [new Map()] as const,
  target: epgChannelsModel.$all,
})

// need to create new maps, instead of reinit, because this will leave old maps in place
sample({
  clock: reset,
  fn: () => [new Map(), new Map()] as const,
  target: epgChannelsModel.$byCategory,
})

// need to create new map, instead of reinit, because this will leave old map in place
sample({
  clock: reset,
  fn: () => [new Map()] as const,
  target: loadEpgChannelsModel.$pending,
})
